import {Observable, Subscriber} from 'rxjs';

/** Evento SSE: `type` è il nome della riga `event:` (`message` se assente), `data` le righe `data:` unite. */
export interface SseEvento {
  type: string;
  data: string;
}

export interface SseRichiesta {
  url: string;
  method?: 'GET' | 'POST';
  token?: string;
  body?: unknown;
}

/** Risposta non 2xx prima dell'apertura dello stream: `body` è il testo restituito dal server. */
export class SseHttpError extends Error {
  constructor(public readonly status: number, public readonly body: string) {
    super(`HTTP ${status}`);
  }
}

/**
 * Stream SSE via `fetch` (feature 159): `EventSource` non permette né POST né l'header Authorization,
 * e le richieste in streaming non passano dagli interceptor HTTP. Annullare la sottoscrizione chiude la
 * connessione (AbortController); lo stream completa quando il server chiude la risposta.
 */
export function sseStream(richiesta: SseRichiesta): Observable<SseEvento> {
  return new Observable<SseEvento>(subscriber => {
    const controller = new AbortController();
    fetch(richiesta.url, opzioniFetch(richiesta, controller.signal))
      .then(response => leggiRisposta(response, subscriber))
      .catch(errore => {
        if (!controller.signal.aborted) subscriber.error(errore);
      });
    return () => controller.abort();
  });
}

function opzioniFetch(richiesta: SseRichiesta, signal: AbortSignal): RequestInit {
  const headers: Record<string, string> = {Accept: 'text/event-stream'};
  if (richiesta.token) headers['Authorization'] = `Bearer ${richiesta.token}`;
  if (richiesta.body !== undefined) headers['Content-Type'] = 'application/json';
  const body = richiesta.body !== undefined ? JSON.stringify(richiesta.body) : undefined;
  return {method: richiesta.method ?? 'GET', headers, body, signal};
}

async function leggiRisposta(response: Response, subscriber: Subscriber<SseEvento>): Promise<void> {
  if (!response.ok || !response.body) {
    subscriber.error(new SseHttpError(response.status, await response.text().catch(() => '')));
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (let letto = await reader.read(); !letto.done && !subscriber.closed; letto = await reader.read()) {
    buffer = emettiBlocchi(buffer + decoder.decode(letto.value, {stream: true}), subscriber);
  }
  subscriber.complete();
}

// Emette i blocchi completi e restituisce il resto (un evento può arrivare spezzato su più chunk).
function emettiBlocchi(buffer: string, subscriber: Subscriber<SseEvento>): string {
  const blocchi = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const resto = blocchi.pop() ?? '';
  blocchi.map(parseBloccoSse).forEach(evento => {
    if (evento) subscriber.next(evento);
  });
  return resto;
}

/** Interpreta un blocco SSE (righe separate da `\n`); `null` se non contiene righe `data:`. */
export function parseBloccoSse(blocco: string): SseEvento | null {
  let type = 'message';
  const dati: string[] = [];
  for (const riga of blocco.split('\n')) {
    if (riga.startsWith('event:')) type = valoreCampo(riga, 'event:');
    if (riga.startsWith('data:')) dati.push(valoreCampo(riga, 'data:'));
  }
  return dati.length ? {type, data: dati.join('\n')} : null;
}

// Lo standard SSE toglie un solo spazio dopo i due punti.
function valoreCampo(riga: string, campo: string): string {
  const valore = riga.slice(campo.length);
  return valore.startsWith(' ') ? valore.slice(1) : valore;
}
